import { useCallback, useEffect, useState } from 'react';
import { useOrganization } from '../../../../contexts/OrganizationContext';
import { organizationPixRepository } from '../../../../infrastructure/repositories/SupabaseOrganizationPixRepository';
import type { OrganizationPixKey, OrganizationPixKeyInput } from '../../../../domain/pix/types';
import { supabase } from '../../../../lib/supabase';

export function useOrganizationPixKey() {
  const { currentOrganization } = useOrganization();
  const [pixKey, setPixKey] = useState<OrganizationPixKey | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!currentOrganization?.id) {
      setPixKey(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const row = await organizationPixRepository.findByOrganizationId(currentOrganization.id);
      setPixKey(row);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao carregar chave PIX');
      setPixKey(null);
    } finally {
      setLoading(false);
    }
  }, [currentOrganization?.id]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const save = useCallback(async (input: OrganizationPixKeyInput) => {
    if (!currentOrganization?.id) {
      throw new Error('Organização não encontrada');
    }
    const { data: { user } } = await supabase.auth.getUser();
    const saved = await organizationPixRepository.upsert(
      currentOrganization.id,
      input,
      user?.id ?? null,
    );
    setPixKey(saved);
    return saved;
  }, [currentOrganization?.id]);

  const remove = useCallback(async () => {
    if (!currentOrganization?.id) {
      throw new Error('Organização não encontrada');
    }
    await organizationPixRepository.deleteByOrganizationId(currentOrganization.id);
    setPixKey(null);
  }, [currentOrganization?.id]);

  return { pixKey, loading, error, refetch, save, remove };
}
